import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// CHECK SETS AND READINGS (doc/check-sets-plan.md, Round A) — the tenant web's
// half of `/api/check-sets` and `/api/check-readings`.
//
// A CHECK SET is a list of fields a doctor (their own) or the hospital admin
// (the hospital's) defines — any fields, any speciality; nothing is shipped
// pre-made. A READING is one filled-in set on one visit. Staff record with a
// direct POST; the visit's doctor types through the drafts system and commits
// with notes and prescription (appointmentDraftSlice), and this slice is the
// read side of what that wrote.
//
//   GET    /check-sets?purpose=manage|record&doctorId=  → { purpose, doctorId, enabled, mine, hospital }
//   POST   /check-sets                                  → 201 { set }
//   PUT    /check-sets/:setId                           → { set }
//   DELETE /check-sets/:setId                           → { set } (soft)
//   POST   /check-sets/:setId/copy                      → 201 { set, copiedFrom }
//   GET|PUT /check-sets/settings/:doctorId              → { doctorId, enabled }
//   GET    /check-readings/appointment/:appointmentId   → { sets, readings, superseded, previous, requiredMissing, … }
//   POST   /check-readings                              → 201 { reading, supersededReadingId }
//   POST   /check-readings/:readingId/correct           → 201 { reading, supersededReadingId }
//
// Every refusal keeps the server's `code`, `field` (a field KEY on a value
// refusal) and `index` (a position in `fields` on a definition refusal), so a
// form can put the server's own sentence next to the input it names.

const serializeError = (error) => {
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
        return {
            code: data.code || null,
            message: data.message || null,
            status: error.response?.status || null,
            field: data.field ?? null,
            index: typeof data.index === 'number' ? data.index : null,
            section: data.section ?? null,
            sets: Array.isArray(data.sets) ? data.sets : null,
            supersededBy: data.supersededBy || null,
            permission: data.permission || null,
            feature: data.feature || null,
        };
    }
    return { code: null, message: error?.message || 'Request failed', status: error?.response?.status || null };
};

const byName = (a, b) => String(a?.name || '').localeCompare(String(b?.name || ''));

export const fetchCheckSets = createAsyncThunk(
    'checkSets/fetch',
    async ({ purpose = 'manage', doctorId = null } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ purpose });
            if (doctorId) params.set('doctorId', doctorId);
            const res = await axiosInstance.get(`${baseURL}/check-sets?${params.toString()}`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const createCheckSet = createAsyncThunk(
    'checkSets/create',
    async ({ body }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/check-sets`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const updateCheckSet = createAsyncThunk(
    'checkSets/update',
    async ({ setId, changes }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/check-sets/${encodeURIComponent(setId)}`, changes);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const deleteCheckSet = createAsyncThunk(
    'checkSets/delete',
    async ({ setId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.delete(`${baseURL}/check-sets/${encodeURIComponent(setId)}`);
            return { setId, set: res.data?.set || null };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const copyCheckSet = createAsyncThunk(
    'checkSets/copy',
    async ({ setId, doctorId, name }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = {};
            if (doctorId) body.doctorId = doctorId;
            if (name) body.name = name;
            const res = await axiosInstance.post(`${baseURL}/check-sets/${encodeURIComponent(setId)}/copy`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// The doctor's own switch — doctor_settings.prescriptionSections.checkSets
// server-side (absent = off). doctorSlice listens to both answers so the
// cached doctorSettings agrees with it.
export const fetchCheckSwitch = createAsyncThunk(
    'checkSets/fetchSwitch',
    async ({ doctorId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/check-sets/settings/${encodeURIComponent(doctorId)}`);
            return { doctorId: res.data?.doctorId || doctorId, enabled: res.data?.enabled === true };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const saveCheckSwitch = createAsyncThunk(
    'checkSets/saveSwitch',
    async ({ doctorId, enabled }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/check-sets/settings/${encodeURIComponent(doctorId)}`, { enabled: enabled === true });
            return { doctorId: res.data?.doctorId || doctorId, enabled: res.data?.enabled === true };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// One visit's readings, the sets offered on it, the previous reading of each
// set (the nurse's one "Last: …" line, §8-2) and `requiredMissing` — exactly
// what the completion gate would refuse.
export const fetchVisitReadings = createAsyncThunk(
    'checkSets/fetchVisit',
    async ({ appointmentId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/check-readings/appointment/${encodeURIComponent(appointmentId)}`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    },
    {
        // Several parts of a screen ask for the same visit on mount; one
        // request answers all of them. `force` re-reads after a write.
        condition: ({ appointmentId, force = false } = {}, { getState }) => {
            if (!appointmentId) return false;
            if (force) return true;
            const v = getState().checkSets?.visits?.[appointmentId];
            return !(v && (v.loading || v.loaded));
        },
    }
);

export const recordReading = createAsyncThunk(
    'checkSets/record',
    async ({ appointmentId, setId, values }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/check-readings`, { appointmentId, setId, values });
            dispatch(fetchVisitReadings({ appointmentId, force: true }));
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const correctReading = createAsyncThunk(
    'checkSets/correct',
    async ({ readingId, appointmentId, values }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/check-readings/${encodeURIComponent(readingId)}/correct`, { values });
            if (appointmentId) dispatch(fetchVisitReadings({ appointmentId, force: true }));
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

const emptyManage = () => ({
    purpose: null,
    doctorId: null,
    enabled: null,
    mine: [],
    hospital: [],
    loading: false,
    loaded: false,
    error: null,
});

const replaceIn = (list, set) => {
    const at = list.findIndex((s) => s.setId === set.setId);
    if (at >= 0) list[at] = set;
    else list.push(set);
    list.sort(byName);
};

// `manage` holds ONE list at a time: the doctor's own, the hospital's, or —
// for the hospital admin — the doctor picked on the hospital page. An answer
// for a list that is no longer on screen (the admin picked another doctor
// before it landed) must not overwrite the one that is.
const sameScope = (manage, arg) => {
    const { purpose = 'manage', doctorId = null } = arg || {};
    return manage.purpose === purpose && manage.doctorId === doctorId;
};

// A doctor set belongs in `mine` only when `mine` is that doctor's list:
// the signed-in doctor's own (asked without a doctorId) or the admin's pick.
const mineFits = (manage, set) => manage.doctorId === null || manage.doctorId === set.doctorId;

const checkSetSlice = createSlice({
    name: 'checkSets',
    initialState: {
        manage: emptyManage(),
        saving: false,
        // The set a delete / copy is running on (its button spins).
        busyId: null,
        switchState: { doctorId: null, enabled: null, loading: false, saving: false, error: null },
        // appointmentId → { loading, loaded, error, data }
        visits: {},
        // appointmentId → true while a reading is being written
        recording: {},
    },
    reducers: {
        clearVisitReadings(state, action) {
            const id = action?.payload;
            if (id) delete state.visits[id];
            else state.visits = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCheckSets.pending, (state, action) => {
                const { purpose = 'manage', doctorId = null } = action.meta.arg || {};
                const same = state.manage.purpose === purpose && state.manage.doctorId === doctorId;
                if (!same) state.manage = { ...emptyManage(), purpose, doctorId };
                state.manage.loading = true;
                state.manage.error = null;
            })
            .addCase(fetchCheckSets.fulfilled, (state, action) => {
                if (!sameScope(state.manage, action.meta.arg)) return;
                const p = action.payload || {};
                state.manage.loading = false;
                state.manage.loaded = true;
                state.manage.enabled = typeof p.enabled === 'boolean' ? p.enabled : null;
                state.manage.mine = [...(p.mine || [])].sort(byName);
                state.manage.hospital = [...(p.hospital || [])].sort(byName);
            })
            .addCase(fetchCheckSets.rejected, (state, action) => {
                if (!sameScope(state.manage, action.meta.arg)) return;
                state.manage.loading = false;
                state.manage.loaded = true;
                state.manage.error = action.payload || { code: null, message: 'Request failed' };
            })
            .addCase(createCheckSet.pending, (state) => { state.saving = true; })
            .addCase(createCheckSet.fulfilled, (state, action) => {
                state.saving = false;
                const set = action.payload?.set;
                if (!set) return;
                if (set.ownerType === 'tenant') replaceIn(state.manage.hospital, set);
                else if (mineFits(state.manage, set)) replaceIn(state.manage.mine, set);
            })
            .addCase(createCheckSet.rejected, (state) => { state.saving = false; })
            .addCase(updateCheckSet.pending, (state) => { state.saving = true; })
            .addCase(updateCheckSet.fulfilled, (state, action) => {
                state.saving = false;
                const set = action.payload?.set;
                if (!set) return;
                if (set.ownerType === 'tenant') replaceIn(state.manage.hospital, set);
                else if (mineFits(state.manage, set)) replaceIn(state.manage.mine, set);
            })
            .addCase(updateCheckSet.rejected, (state) => { state.saving = false; })
            .addCase(deleteCheckSet.pending, (state, action) => { state.busyId = action.meta.arg.setId; })
            .addCase(deleteCheckSet.fulfilled, (state, action) => {
                state.busyId = null;
                const { setId } = action.payload;
                state.manage.mine = state.manage.mine.filter((s) => s.setId !== setId);
                state.manage.hospital = state.manage.hospital.filter((s) => s.setId !== setId);
            })
            .addCase(deleteCheckSet.rejected, (state) => { state.busyId = null; })
            .addCase(copyCheckSet.pending, (state, action) => { state.busyId = action.meta.arg.setId; })
            .addCase(copyCheckSet.fulfilled, (state, action) => {
                state.busyId = null;
                const set = action.payload?.set;
                if (set && mineFits(state.manage, set)) replaceIn(state.manage.mine, set);
            })
            .addCase(copyCheckSet.rejected, (state) => { state.busyId = null; })
            .addCase(fetchCheckSwitch.pending, (state, action) => {
                state.switchState.doctorId = action.meta.arg.doctorId;
                state.switchState.loading = true;
                state.switchState.error = null;
            })
            .addCase(fetchCheckSwitch.fulfilled, (state, action) => {
                state.switchState.loading = false;
                state.switchState.doctorId = action.payload.doctorId;
                state.switchState.enabled = action.payload.enabled;
            })
            .addCase(fetchCheckSwitch.rejected, (state, action) => {
                state.switchState.loading = false;
                state.switchState.error = action.payload || null;
            })
            .addCase(saveCheckSwitch.pending, (state) => {
                state.switchState.saving = true;
                state.switchState.error = null;
            })
            .addCase(saveCheckSwitch.fulfilled, (state, action) => {
                state.switchState.saving = false;
                state.switchState.doctorId = action.payload.doctorId;
                state.switchState.enabled = action.payload.enabled;
                if (state.manage.doctorId === null || state.manage.doctorId === action.payload.doctorId) {
                    if (state.manage.enabled !== null) state.manage.enabled = action.payload.enabled;
                }
            })
            .addCase(saveCheckSwitch.rejected, (state, action) => {
                state.switchState.saving = false;
                state.switchState.error = action.payload || null;
            })
            .addCase(fetchVisitReadings.pending, (state, action) => {
                const { appointmentId } = action.meta.arg;
                const prev = state.visits[appointmentId];
                // A re-read keeps what is on screen until the answer lands.
                state.visits[appointmentId] = { data: prev?.data || null, loaded: prev?.loaded || false, loading: true, error: null };
            })
            .addCase(fetchVisitReadings.fulfilled, (state, action) => {
                const { appointmentId } = action.meta.arg;
                state.visits[appointmentId] = { data: action.payload, loaded: true, loading: false, error: null };
            })
            .addCase(fetchVisitReadings.rejected, (state, action) => {
                const { appointmentId } = action.meta.arg;
                const prev = state.visits[appointmentId];
                state.visits[appointmentId] = { data: prev?.data || null, loaded: true, loading: false, error: action.payload || { code: null } };
            })
            .addCase(recordReading.pending, (state, action) => { state.recording[action.meta.arg.appointmentId] = true; })
            .addCase(recordReading.fulfilled, (state, action) => { delete state.recording[action.meta.arg.appointmentId]; })
            .addCase(recordReading.rejected, (state, action) => { delete state.recording[action.meta.arg.appointmentId]; })
            .addCase(correctReading.pending, (state, action) => {
                if (action.meta.arg.appointmentId) state.recording[action.meta.arg.appointmentId] = true;
            })
            .addCase(correctReading.fulfilled, (state, action) => { delete state.recording[action.meta.arg.appointmentId]; })
            .addCase(correctReading.rejected, (state, action) => { delete state.recording[action.meta.arg.appointmentId]; });
    },
});

export const { clearVisitReadings } = checkSetSlice.actions;
export default checkSetSlice.reducer;
